import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Background,
  Controls,
  Handle,
  MarkerType,
  MiniMap,
  Position,
  ReactFlow,
  type Edge,
  type Node,
  type NodeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import type {
  Application,
  ApplicationCloudGraph,
  CloudNode,
  Discovery,
  SeedReason,
} from '@ai-secure-twin/shared';
import { api, ApiError } from '../../lib/api';
import {
  CLOUD_NODE_HEIGHT,
  CLOUD_NODE_WIDTH,
  EDGE_KIND,
  NODE_TYPE,
  layoutCloudGraph,
  visibleGraph,
  type GraphFilters,
} from '../../lib/cloud-graph';

const date = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

const inFlight = (d: Discovery | undefined) => d?.status === 'QUEUED' || d?.status === 'RUNNING';

/**
 * The application's cloud account, as discovery found it (ADR 0005).
 *
 * Everything drawn here was read from configuration and carries its evidence. "This application"
 * is the deterministic seed — what the application's own hosts lead to — not a judgement; the AI
 * boundary proposal and a person's confirmation come next (phase 3).
 */
export function CloudView({
  application,
  mayWrite,
}: {
  application: Application;
  mayWrite: boolean;
}) {
  if (!application.cloudConnectionId) {
    return <LinkConnection application={application} mayWrite={mayWrite} />;
  }
  return (
    <Discovered
      application={application}
      connectionId={application.cloudConnectionId}
      mayWrite={mayWrite}
    />
  );
}

function LinkConnection({
  application,
  mayWrite,
}: {
  application: Application;
  mayWrite: boolean;
}) {
  const queryClient = useQueryClient();
  const connections = useQuery({
    queryKey: ['cloudConnections'],
    queryFn: api.listCloudConnections,
  });
  const verified = (connections.data?.items ?? []).filter((c) => c.status === 'VERIFIED');
  const [choice, setChoice] = useState('');

  const link = useMutation({
    mutationFn: (connectionId: string) =>
      api.patchApplication(application.id, {
        expectedVersion: application.version,
        cloudConnectionId: connectionId,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['applications'] }),
  });

  return (
    <section className="card panel">
      <h2 className="panel-title">Cloud architecture</h2>
      <p className="muted" style={{ maxWidth: '72ch' }}>
        Link {application.name} to the AWS account it runs in, and discovery will map the resources
        behind its hosts — CDN, APIs, functions, data stores and who can reach what.
      </p>
      {connections.isLoading ? (
        <p className="muted">Loading cloud accounts…</p>
      ) : verified.length === 0 ? (
        <p className="muted">
          No verified cloud account yet. A security administrator connects one under{' '}
          <Link className="table-link" to="/app/cloud">
            Cloud accounts
          </Link>
          .
        </p>
      ) : mayWrite ? (
        <div className="link-connection">
          <select
            className="input"
            aria-label="Cloud account"
            value={choice}
            onChange={(e) => setChoice(e.target.value)}
          >
            <option value="">Select a cloud account…</option>
            {verified.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} · {c.accountId} · {c.regions.join(', ')}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!choice || link.isPending}
            onClick={() => link.mutate(choice)}
          >
            Link account
          </button>
        </div>
      ) : (
        <p className="muted">Someone who can edit applications links it to a cloud account.</p>
      )}
      {link.error ? (
        <p className="login-error" role="alert">
          {link.error instanceof ApiError ? link.error.message : 'Could not link the account.'}
        </p>
      ) : null}
    </section>
  );
}

function Discovered({
  application,
  connectionId,
  mayWrite,
}: {
  application: Application;
  connectionId: string;
  mayWrite: boolean;
}) {
  const queryClient = useQueryClient();
  const runs = useQuery({
    queryKey: ['discoveries', connectionId],
    queryFn: () => api.listDiscoveries(connectionId),
    refetchInterval: (q) => (inFlight(q.state.data?.items[0]) ? 5000 : false),
  });
  const latest = runs.data?.items[0];
  const lastGood = runs.data?.items.find((d) => d.status === 'SUCCEEDED');

  const graph = useQuery({
    queryKey: ['cloudGraph', application.id, lastGood?.id],
    queryFn: () => api.getCloudGraph(application.id),
    enabled: Boolean(lastGood),
  });

  const start = useMutation({
    mutationFn: () => api.startDiscovery(connectionId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['discoveries', connectionId] }),
  });

  return (
    <>
      <section className="card panel">
        <div className="panel-head">
          <h2 className="panel-title">Cloud architecture</h2>
          {mayWrite ? (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={start.isPending || inFlight(latest)}
              onClick={() => start.mutate()}
            >
              {inFlight(latest) ? 'Discovering…' : 'Discover now'}
            </button>
          ) : null}
        </div>
        <DiscoveryStatus latest={latest} lastGood={lastGood} data={graph.data} />
        {start.error ? (
          <p className="login-error" role="alert">
            {start.error instanceof ApiError ? start.error.message : 'Could not start a discovery.'}
          </p>
        ) : null}
      </section>
      {graph.isLoading ? <p className="muted">Loading the graph…</p> : null}
      {graph.isError ? (
        <p className="login-error" role="alert">
          {graph.error instanceof ApiError ? graph.error.message : 'Could not load the graph.'}
        </p>
      ) : null}
      {graph.data ? <GraphCanvas data={graph.data} /> : null}
    </>
  );
}

function DiscoveryStatus({
  latest,
  lastGood,
  data,
}: {
  latest: Discovery | undefined;
  lastGood: Discovery | undefined;
  data: ApplicationCloudGraph | undefined;
}) {
  if (!latest) {
    return (
      <p className="muted">Not discovered yet. Discovery reads configuration only — never data.</p>
    );
  }
  return (
    <div className="discovery-status">
      {inFlight(latest) ? (
        <p role="status">
          <span className="status status-waiting">
            {latest.status === 'QUEUED' ? 'Queued' : 'Discovering'}
          </span>{' '}
          requested {date(latest.requestedAt)}. This takes a few minutes.
        </p>
      ) : latest.status === 'FAILED' ? (
        <p className="login-error" role="alert">
          The last discovery failed: {latest.error ?? 'unknown reason'}
        </p>
      ) : null}
      {lastGood ? (
        <p className="muted">
          Showing the discovery of {date(lastGood.finishedAt ?? lastGood.requestedAt)}:{' '}
          {lastGood.nodeCount} resources, {lastGood.edgeCount} relationships.
          {data?.integrity === 'changed' ? (
            <strong className="sev-high">
              {' '}
              The stored graph no longer matches its recorded hash.
            </strong>
          ) : null}
          {data?.changes
            ? ` Since the discovery before: +${data.changes.addedNodes.length} new, −${data.changes.removedNodes.length} gone.`
            : ''}
        </p>
      ) : null}
      {lastGood && lastGood.gaps.length > 0 ? (
        <details className="policy-details">
          <summary>{lastGood.gaps.length} services could not be read fully</summary>
          <ul className="mono" style={{ fontSize: 12 }}>
            {lastGood.gaps.map((g) => (
              <li key={g}>{g}</li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

interface ResourceData extends Record<string, unknown> {
  node: CloudNode;
  inSeed: boolean;
  isNew: boolean;
}

function ResourceNode({ data, selected }: NodeProps<Node<ResourceData>>) {
  const type = NODE_TYPE[data.node.type];
  return (
    <div
      className={`cloud-node cat-${type.category}${data.inSeed ? ' in-seed' : ''}${selected ? ' is-selected' : ''}`}
      style={{ width: CLOUD_NODE_WIDTH, height: CLOUD_NODE_HEIGHT }}
      title={data.node.id}
    >
      <Handle type="target" position={Position.Left} />
      <span className="cloud-node-badge">{type.short}</span>
      <span className="cloud-node-text">
        <span className="cloud-node-name">{data.node.name}</span>
        <span className="cloud-node-kind">
          {type.label}
          {data.node.public ? <span className="cloud-public"> · public</span> : null}
          {data.isNew ? <span className="change-new"> new</span> : null}
        </span>
      </span>
      <Handle type="source" position={Position.Right} />
    </div>
  );
}

const nodeTypes = { resource: ResourceNode };

function GraphCanvas({ data }: { data: ApplicationCloudGraph }) {
  const [filters, setFilters] = useState<GraphFilters>({
    applicationOnly: data.seed.length > 0,
    showPermissions: false,
  });
  const [selected, setSelected] = useState<string | null>(null);

  const seedById = useMemo(() => new Map(data.seed.map((s) => [s.nodeId, s])), [data.seed]);
  const added = useMemo(() => new Set(data.changes?.addedNodes ?? []), [data.changes]);

  const { nodes, edges } = useMemo(() => {
    const visible = visibleGraph(data.graph, data.seed, filters);
    const positions = layoutCloudGraph(visible.nodes, visible.edges);
    const flowNodes: Node<ResourceData>[] = visible.nodes.map((n) => ({
      id: n.id,
      type: 'resource',
      position: positions.get(n.id) ?? { x: 0, y: 0 },
      data: { node: n, inSeed: seedById.has(n.id), isNew: added.has(n.id) },
    }));
    const flowEdges: Edge[] = visible.edges.map((e) => ({
      id: `${e.from}>${e.to}>${e.kind}`,
      source: e.from,
      target: e.to,
      className: `cloud-edge kind-${e.kind.toLowerCase()}`,
      style: EDGE_KIND[e.kind].dashed ? { strokeDasharray: '5 5' } : undefined,
      markerEnd: { type: MarkerType.ArrowClosed, width: 14, height: 14 },
    }));
    return { nodes: flowNodes, edges: flowEdges };
  }, [data.graph, data.seed, filters, seedById, added]);

  const focus = selected ? data.graph.nodes.find((n) => n.id === selected) : undefined;

  return (
    <section className="card panel">
      <div className="panel-head">
        <div className="filters" role="group" aria-label="What to show">
          <button
            type="button"
            className="filter"
            aria-pressed={filters.applicationOnly}
            disabled={data.seed.length === 0}
            onClick={() => setFilters({ ...filters, applicationOnly: true })}
          >
            This application ({data.seed.length})
          </button>
          <button
            type="button"
            className="filter"
            aria-pressed={!filters.applicationOnly}
            onClick={() => setFilters({ ...filters, applicationOnly: false })}
          >
            Whole account ({data.graph.nodes.length})
          </button>
          <label className="check" style={{ marginLeft: 12 }}>
            <input
              type="checkbox"
              checked={filters.showPermissions}
              onChange={(e) => setFilters({ ...filters, showPermissions: e.target.checked })}
            />
            Roles and permissions
          </label>
        </div>
      </div>
      {data.seed.length === 0 ? (
        <p className="demo-notice">
          None of this application&rsquo;s hosts (
          {data.entrypointHosts.join(', ') || 'none declared'}) matched a resource in the account,
          so the whole account is shown. Declare component hosts or scope the engagement so the map
          can tell which resources are this application&rsquo;s.
        </p>
      ) : (
        <p className="muted surface-basis">
          &ldquo;This application&rdquo; is what its hosts ({data.entrypointHosts.join(', ')}) lead
          to through configuration, plus the rest of the CloudFormation stacks they are in.
          Evidence, not a verdict.
        </p>
      )}
      <div className="cloud-canvas">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          fitView
          minZoom={0.1}
          nodesDraggable={false}
          nodesConnectable={false}
          onNodeClick={(_, n) => setSelected(n.id)}
          onPaneClick={() => setSelected(null)}
          proOptions={{ hideAttribution: true }}
        >
          <Background gap={24} size={1} />
          <Controls showInteractive={false} />
          <MiniMap pannable zoomable className="cloud-minimap" />
        </ReactFlow>
      </div>
      {focus ? (
        <ResourceDetail
          node={focus}
          data={data}
          seed={seedById.get(focus.id)}
          onSelect={setSelected}
        />
      ) : (
        <p className="muted" style={{ fontSize: 13 }}>
          Select a resource to see its configuration and the evidence for each relationship.
        </p>
      )}
    </section>
  );
}

function seedText(reason: SeedReason, name: (id: string) => string): string {
  if (reason.kind === 'ENTRYPOINT') return `Serves the application host ${reason.via}.`;
  if (reason.kind === 'SAME_STACK')
    return `In stack ${reason.via}, with ${name(reason.from ?? '')}.`;
  return `Reached from ${name(reason.from ?? '')} (${EDGE_KIND[reason.via as keyof typeof EDGE_KIND]?.label ?? reason.via}).`;
}

function ResourceDetail({
  node,
  data,
  seed,
  onSelect,
}: {
  node: CloudNode;
  data: ApplicationCloudGraph;
  seed: SeedReason | undefined;
  onSelect: (id: string) => void;
}) {
  const byId = new Map(data.graph.nodes.map((n) => [n.id, n]));
  const name = (id: string) => byId.get(id)?.name ?? id;
  const related = data.graph.edges.filter((e) => e.from === node.id || e.to === node.id);
  return (
    <div className="resource-detail">
      <h3 className="detail-title">
        {node.name} <span className="muted">· {NODE_TYPE[node.type].label}</span>
      </h3>
      <p className="mono muted" style={{ fontSize: 12, wordBreak: 'break-all' }}>
        {node.id}
      </p>
      {seed ? (
        <p>{seedText(seed, name)}</p>
      ) : (
        <p className="muted">Not reached from this application&rsquo;s hosts.</p>
      )}
      <dl className="kv">
        <dt>Region</dt>
        <dd>{node.region}</dd>
        <dt>Stack</dt>
        <dd className="mono">{node.stack ?? '—'}</dd>
        <dt>Public</dt>
        <dd>{node.public ? 'Yes — reachable from the internet by configuration' : 'No'}</dd>
        {Object.entries(node.attributes).map(([k, v]) => (
          <div key={k} style={{ display: 'contents' }}>
            <dt>{k}</dt>
            <dd className="mono">{String(v)}</dd>
          </div>
        ))}
        {Object.entries(node.tags).map(([k, v]) => (
          <div key={`tag-${k}`} style={{ display: 'contents' }}>
            <dt>tag {k}</dt>
            <dd className="mono">{v}</dd>
          </div>
        ))}
      </dl>
      {related.length > 0 ? (
        <>
          <h3 className="detail-title">Relationships ({related.length})</h3>
          <ul className="relationship-list">
            {related.map((e) => {
              const other = e.from === node.id ? e.to : e.from;
              const outgoing = e.from === node.id;
              return (
                <li key={`${e.from}>${e.to}>${e.kind}`}>
                  <span>
                    {outgoing ? EDGE_KIND[e.kind].label : `${EDGE_KIND[e.kind].label} ←`}{' '}
                    <button type="button" className="link-btn" onClick={() => onSelect(other)}>
                      {name(other)}
                    </button>
                  </span>
                  <span className="mono muted evidence">
                    {e.evidence.api} · {e.evidence.field}
                    {e.evidence.detail ? ` · ${e.evidence.detail}` : ''}
                  </span>
                </li>
              );
            })}
          </ul>
        </>
      ) : null}
    </div>
  );
}
